import { forwardRef } from 'react';
import MuiTable, { type TableProps } from '@mui/material/Table';
import MuiTableBody, { type TableBodyProps } from '@mui/material/TableBody';
import MuiTableCell, { type TableCellProps } from '@mui/material/TableCell';
import MuiTableContainer, {
  type TableContainerProps as MuiTableContainerProps,
} from '@mui/material/TableContainer';
import MuiTableFooter, { type TableFooterProps } from '@mui/material/TableFooter';
import MuiTableHead, { type TableHeadProps } from '@mui/material/TableHead';
import MuiTableRow, { type TableRowProps } from '@mui/material/TableRow';
import type { SxProps, Theme } from '@mui/material/styles';
import Paper from '../primitives/Paper';
import { mergeSx } from '../utils/mergeSx';
import { table as tableTokens } from '@/shared/theme/tokens';

export type { TableProps };
export type { TableBodyProps };
export type { TableCellProps };
/** Default root is Paper, so elevation and variant are valid. */
export type TableContainerProps = MuiTableContainerProps<typeof Paper>;
export type { TableFooterProps };
export type { TableHeadProps };
export type { TableRowProps };

const headCellSx: SxProps<Theme> = {
  bgcolor: 'background.paper',
  color: 'text.secondary',
  borderBottom: 1,
  borderColor: 'divider',
  whiteSpace: 'nowrap',
  fontSize: tableTokens.headerFontSize,
  fontWeight: tableTokens.headerFontWeight,
  lineHeight: tableTokens.headerLineHeight,
  letterSpacing: tableTokens.headerLetterSpacing,
  padding: `${tableTokens.headerPaddingY} ${tableTokens.headerPaddingX}`,
  height: tableTokens.headerRowHeight,
  maxHeight: tableTokens.headerRowHeight,
  boxSizing: 'border-box',
  verticalAlign: 'middle',
  overflow: 'hidden',
};

const bodyCellSx: SxProps<Theme> = {
  color: 'text.primary',
  borderColor: 'divider',
  fontSize: tableTokens.bodyFontSize,
  padding: `${tableTokens.bodyPaddingY} ${tableTokens.bodyPaddingX}`,
  '&:first-of-type': {
    paddingLeft: tableTokens.bodyPaddingXFirst,
  },
};

const Table = ({ size = 'small', stickyHeader = true, sx, ...props }: TableProps) => (
  <MuiTable size={size} stickyHeader={stickyHeader} sx={sx} {...props} />
);

const TableContainer = forwardRef<HTMLDivElement, TableContainerProps>(
  function TableContainer({ component = Paper, sx, ...props }, ref) {
    return (
      <MuiTableContainer
        ref={ref}
        component={component}
        sx={mergeSx(
          {
            flex: 1,
            minHeight: 0,
            height: '100%',
            overflow: 'auto',
            bgcolor: 'background.paper',
          },
          sx,
        )}
        {...props}
      />
    );
  },
);

const TableHead = ({ sx, ...props }: TableHeadProps) => (
  <MuiTableHead
    sx={mergeSx({ '& .MuiTableCell-root.MuiTableCell-head': headCellSx }, sx)}
    {...props}
  />
);

const TableBody = ({ sx, ...props }: TableBodyProps) => (
  <MuiTableBody
    sx={mergeSx(
      {
        '& .MuiTableRow-root:last-child .MuiTableCell-root': {
          borderBottom: 0,
        },
      },
      sx,
    )}
    {...props}
  />
);

const TableFooter = ({ sx, ...props }: TableFooterProps) => (
  <MuiTableFooter
    sx={mergeSx(
      {
        position: 'sticky',
        bottom: 0,
        zIndex: 2,
        '& .MuiTableCell-root.MuiTableCell-footer': {
          borderTop: 1,
          borderColor: 'divider',
          borderBottom: 0,
          bgcolor: 'background.paper',
          color: 'text.secondary',
          minHeight: tableTokens.footerMinHeight,
          height: tableTokens.footerMinHeight,
          padding: `${tableTokens.footerPaddingY} ${tableTokens.footerPaddingX}`,
          '& .MuiTablePagination-root': {
            minHeight: tableTokens.paginationMinHeight,
            width: '100%',
          },
          '& .MuiToolbar-root': {
            minHeight: tableTokens.paginationMinHeight,
            height: tableTokens.paginationMinHeight,
            pl: 0,
            pr: 0,
          },
        },
      },
      sx,
    )}
    {...props}
  />
);

const TableRow = ({ sx, ...props }: TableRowProps) => (
  <MuiTableRow
    sx={mergeSx(
      {
        '&.MuiTableRow-hover:hover:not(.Mui-selected)': {
          bgcolor: 'action.hover',
        },
      },
      sx,
    )}
    {...props}
  />
);

const TableCell = ({ sx, ...props }: TableCellProps) => (
  <MuiTableCell
    sx={mergeSx(
      {
        borderColor: 'divider',
        '&.MuiTableCell-head': headCellSx,
        '&:not(.MuiTableCell-head):not(.MuiTableCell-footer)': bodyCellSx,
      },
      sx,
    )}
    {...props}
  />
);

export { Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow };
